import React, {useEffect, useState} from 'react';
import st from './Teams.module.scss';
import Table from '../../components/Table/Table';
import ModalForm from '../../components/ModalForm/ModalForm';
import {Input, Button, Popconfirm, notification, Select, InputNumber, Tabs} from "antd";
import api from '../../config/axiosConfig';
import {EditOutlined, DeleteOutlined, ExportOutlined} from '@ant-design/icons';
import {exportToExcel, FilterDropdown, FilterSearch, NumberRangeFilter} from "../../utils/utils";

const Teams = () => {
    const [coaches, setCoaches] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [teamIsUpdated, setTeamIsUpdated] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentTeam, setCurrentTeam] = useState(null);
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [nestedTableData, setNestedTableData] = useState([]);

    const handleDataLoaded = (data) => {
        setTableData(data);
    };

    const handleNestedDataLoaded = (data) => {
        setNestedTableData(data);
    }

    const handleExport = (key, record) => {
        if (key === 'nested' ? (!nestedTableData || nestedTableData.length === 0) : (!tableData || tableData.length === 0)){
            notification.error({
                message: 'Error',
                description: 'No data available to export.',
                placement: 'topRight',
            });
            return;
        }

        exportToExcel({
            data: key === 'nested' ? nestedTableData : tableData,
            columns: key === 'nested' ? nestedColumns : columns,
            fileName: key === 'nested' ? record.name : 'Teams_Data',
            coaches,
        });
        notification.success({
            message: 'Success',
            description: 'Teams data exported successfully.',
            placement: 'topRight',
        });
    };

    const coachesList = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.get('coaches', {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            setCoaches(response.data);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch coaches list.',
                placement: 'topRight',
            });
        }
    };

    const handleAddEditTeam = async (values) => {
        setLoading(true);
        try {
            if (currentTeam) {
                // Edit Team
                await api.put(`teams/${currentTeam.id}`, values);
                notification.success({
                    message: 'Success',
                    description: 'Team updated successfully',
                    placement: 'topRight'
                });
            } else {
                // Add Team
                await api.post('teams/create', values);
                notification.success({
                    message: 'Success',
                    description: 'Team added successfully',
                    placement: 'topRight'
                });
            }
            setIsModalOpen(false);
            setTeamIsUpdated(!teamIsUpdated);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to save team. Please try again.',
                placement: 'topRight'
            });
        } finally {
            setLoading(false);
        }
    };

    const openEditModal = (team) => {
        setCurrentTeam(team);
        setEditMode(true);
        setIsModalOpen(true);
    };

    const handleDeleteTeam = async (id) => {
        setLoading(true);
        try {
            await api.delete(`teams/${id}`);
            notification.success({
                message: 'Success',
                description: 'Team has been successfully deleted.',
                placement: 'topRight',
            });
            setTeamIsUpdated(prev => !prev);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to delete the team. Please try again.',
                placement: 'topRight',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        coachesList();
    }, [isModalOpen]);

    const columns = [
        {
            title: 'გუნდის სახელი',
            dataIndex: 'name',
            sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                             }) => (
                <FilterSearch
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Team Name"
                />
            ),
            onFilter: (value, record) => {
                return (record?.name.toLowerCase() || '').includes(value.toLowerCase());
            },
        },
        {
            title: 'მწვრთნელი',
            dataIndex: 'coachId',
            sorter: (a, b) => String(a.coachId || '').localeCompare(String(b.coachId || '')),
            render: (coachId) => {
                const coach = coaches?.find(c => c.id === coachId);
                return coach ? `${coach.firstName} ${coach.lastName}` : 'No Coach';  // Safeguard against undefined coach
            },
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                             }) => (
                <FilterDropdown
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Team"
                    data={coaches}
                />
            ),
            onFilter: (value, record) => {
                return record?.coachId === value;
            },
        },
        {
            title: 'გადასახადი',
            dataIndex: 'serviceFee',
            sorter: (a, b) => (Number(a.serviceFee || 0)) - (Number(b.serviceFee || 0)),
            render: (serviceFee) => {
                return serviceFee + ' ₾';
            },
            filterDropdown: props => <NumberRangeFilter {...props} />,
            onFilter: (value, record) => {
                const {min, max} = JSON.parse(value);
                let minValue = min !== undefined ? min : -Infinity;
                let maxValue = max !== undefined ? max : Infinity;
                if (min !== undefined && max === undefined) {
                    maxValue = minValue;
                } else if (max !== undefined && min === undefined) {
                    minValue = maxValue;
                }
                if (minValue > maxValue) {
                    [minValue, maxValue] = [maxValue, minValue];
                }
                return record.serviceFee >= minValue && record.serviceFee <= maxValue;
            },
        },
        {
            title: 'კომენტარი',
            dataIndex: 'note',
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                             }) => (
                <FilterSearch
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Note"
                />
            ),
            onFilter: (value, record) => {
                return (record?.note.toLowerCase() || '').includes(value.toLowerCase());
            },
        },
        // {
        //     title: 'Gender',
        //     dataIndex: 'gender',
        //     filters: [
        //         {
        //             text: 'Male',
        //             value: 'male',
        //         },
        //         {
        //             text: 'Female',
        //             value: 'female',
        //         },
        //     ],
        //     width: '20%',
        // },
        {
            title: 'Actions',
            key: 'actions',
            render: (text, record) => (
                <>
                    {/* Edit Button */}
                    <Button type="link" onClick={() => openEditModal(record)}>
                        <EditOutlined/>
                    </Button>
                    <Popconfirm
                        title="Are you sure you want to delete this team?"
                        onConfirm={() => handleDeleteTeam(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="link" danger>
                            <DeleteOutlined/>
                        </Button>
                    </Popconfirm>
                </>
            ),
        },
    ];
    const formItems = [
        [{label: 'გუნდის სახელი', name: 'name', rules: [{required: true}], component: <Input/>, span: 24}],
        [{
            label: 'მწვრთნელი',
            name: 'coachId',
            rules: [{required: true, message: 'Please select a coach'}],
            component: (
                <Select
                    showSearch={true}
                    placeholder="Select a coach"
                    filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={coaches.map(coach => ({
                        value: coach.id,
                        label: `${coach.firstName} ${coach.lastName}`
                    }))}
                    allowClear={true}
                />
            ),
            span: 12
        },
            {
                label: 'გადასახადი', name: 'serviceFee', rules: [
                    {required: true, message: 'Please enter a valid fee'},
                    {type: 'number', min: 1, message: 'Value must be at least 1'}
                ], component: <InputNumber/>, span: 12
            }
        ],
        [{label: 'კომენტარი', name: 'note', component: <Input.TextArea/>, span: 24}],
    ];

    const nestedColumns = [
        {title: 'იდენთიფიკატორი', dataIndex: 'identifier', width: 100,},
        {title: 'სახელი', dataIndex: 'firstName', width: 100,},
        {title: 'გვარი', dataIndex: 'lastName', width: 100,},
    ];

    // Expanded row render function
    const expandedRowRender = (record) => (
        <Tabs defaultActiveKey="1">
            <Tabs.TabPane tab="მოთამაშეები" key="1">
                <Table
                    columns={nestedColumns}
                    url={`students/${record.id}/students?active=true&page=1`}
                    pagination={false}
                    size="small"
                    onDataLoaded={handleNestedDataLoaded}
                />
            </Tabs.TabPane>
            <Tabs.TabPane
                disabled
                tab={<Button
                    type="default"
                    icon={<ExportOutlined/>}
                    onClick={() => handleExport('nested', record)}
                >
                    Export to Excel
                </Button>}
            />
        </Tabs>
    );

    const handleRowDoubleClick = (record) => {
        const isExpanded = expandedRowKeys.includes(record.id);
        setExpandedRowKeys(isExpanded ? expandedRowKeys?.filter((key) => key !== record.id) : [...expandedRowKeys, record.id]);
    };

    return (
        <>
            <div className={st.btnContainer}>
                <Button type="primary" onClick={() => {
                    setIsModalOpen(true)
                    setEditMode(false);
                    setCurrentTeam(null);
                }} style={{marginBottom: 16}}>
                    Add Team
                </Button>
                <Button
                    type="default"
                    icon={<ExportOutlined/>}
                    onClick={handleExport}
                    style={{marginBottom: 16, marginLeft: 8}}
                >
                    Export to Excel
                </Button>
            </div>
            <ModalForm
                open={isModalOpen} // Updated to `open`
                title={editMode ? "Edit Team" : "Add New Team"}
                onCancel={() => setIsModalOpen(false)}
                onSubmit={handleAddEditTeam}
                confirmLoading={loading}
                formItems={formItems}
                initialValues={currentTeam}
                rowIsUpdated={teamIsUpdated}
            />
            <Table columns={columns}
                   url='teams'
                   rowIsUpdated={teamIsUpdated}
                   rowKey={(record) => record.id}
                   expandable={{
                       expandedRowRender,
                       expandedRowKeys,
                       onExpandedRowsChange: setExpandedRowKeys,
                   }}
                   onRow={(record) => ({
                       onDoubleClick: () => handleRowDoubleClick(record),
                   })}
                   onDataLoaded={handleDataLoaded}
            />
        </>
    );
}

export default Teams;